// Passing Percentiles widget (Players canvas, `player-passing-percentiles`) —
// old Catalyst's "Percentiles" card, replicated: how the player ranks vs their
// position group on each passing metric, as horizontal 0–100 bars COLOURED by a
// 7-band diverging success scale (weak = warm/pink, strong = cool/cyan). The
// stylistic metric (difficulty appetite) is drawn hatched-grey — a higher rank
// there isn't "better", just a harder-passing choice.
//
// Metric list + colours mirror old Catalyst 1:1. Positional basis; shares the
// peer query. Rank-based (percentileOf), robust to low-minute per-90 outliers.

import { useMemo } from "react";
import { useMetricCategories } from "../data/stats.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { num, percentileOf, peerValues, percentileBand } from "./passing.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

// Old Catalyst's PERCENTILE_KEYS, in order.
const KEYS = [
  "passes_90",
  "avg_pass_difficulty",
  "psax_perc",
  "pass_success_perc",
  "deep_progressions_90",
  "obv_pass_90",
  "pressured_pass_success_perc",
  "xga_90",
];
// A "style" metric: a higher rank isn't "better", just harder passing.
const STYLE_KEYS = new Set(["avg_pass_difficulty"]);

// 7-band diverging success scale (weakest → strongest), from old Catalyst's
// --sv-sl-* palette (dark theme).
const BAND_COLORS = ["#f04488", "#da6018", "#cc9c18", "#6c7e92", "#96cc28", "#28b460", "#18cce8"];
const NEUTRAL = "#6c7e92";
// Hatched grey for style rows (diagonal stripes) so they don't read as "average".
const HATCH = `repeating-linear-gradient(45deg, ${NEUTRAL} 0 3px, #3f4b5a 3px 6px)`;

export default function PassingPercentilesWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, KEYS);
  const cats = useMetricCategories(true);

  const info = useMemo(() => {
    const map: Record<string, { shortName: string; outputType?: string | null }> = {};
    for (const c of cats.data ?? [])
      for (const s of c.subcategories)
        for (const m of s.playerMetrics) map[m.key] = { shortName: m.shortName, outputType: m.outputType };
    return map;
  }, [cats.data]);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending || cats.isPending) return <Msg>Loading percentiles…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load percentiles.</Msg>;
  if (!cmp.playerRow) return <Msg>No passing data for this player in this season.</Msg>;
  if (!cmp.hasGroup)
    return <Msg>No position group for this player — percentiles need peers to rank against.</Msg>;

  const rows = KEYS.map((key) => {
    const value = num(cmp.playerRow, key);
    const pct = value === null ? null : percentileOf(value, peerValues(cmp.peerRows, key));
    return {
      key,
      label: info[key]?.shortName ?? key,
      valueText: fmtValue(value, info[key]?.outputType),
      pct,
      isStyle: STYLE_KEYS.has(key),
    };
  });
  const hasStyle = rows.some((r) => r.isStyle);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
      <PassingScope cmp={cmp} />
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 7 }}>
        {rows.map((r) => (
          <Bar key={r.key} label={r.label} pct={r.pct} valueText={r.valueText} isStyle={r.isStyle} />
        ))}
      </div>
      <Legend hasStyle={hasStyle} />
    </div>
  );
}

function Bar({ label, pct, valueText, isStyle }: { label: string; pct: number | null; valueText: string; isStyle: boolean }) {
  const width = pct === null ? 0 : Math.max(2, pct); // keep a sliver at 0
  const fill = pct === null ? theme.border : isStyle ? HATCH : BAND_COLORS[percentileBand(pct)];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "104px 48px 1fr 30px", alignItems: "center", gap: 8 }}>
      <div style={{ color: theme.inkMuted, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </div>
      <div style={{ textAlign: "right", fontSize: 11, color: theme.inkMuted, fontVariantNumeric: "tabular-nums" }}>
        {valueText}
      </div>
      <div style={{ position: "relative", height: 12, background: theme.field, borderRadius: 6, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${width}%`, background: fill, borderRadius: 6 }} />
      </div>
      <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: theme.ink, fontVariantNumeric: "tabular-nums" }}>
        {pct === null ? "—" : Math.round(pct)}
      </div>
    </div>
  );
}

/** The colour key: a weak→strong gradient + (if present) the hatched style swatch. */
function Legend({ hasStyle }: { hasStyle: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, color: theme.inkMuted }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>Weaker</span>
        <span style={{ display: "inline-flex", borderRadius: 3, overflow: "hidden" }}>
          {BAND_COLORS.map((c) => (
            <span key={c} style={{ width: 16, height: 8, background: c, display: "inline-block" }} />
          ))}
        </span>
        <span>Stronger</span>
        <span style={{ color: theme.inkFaint }}>— rank vs peer distribution</span>
      </div>
      {hasStyle && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 16, height: 8, background: HATCH, borderRadius: 3, display: "inline-block" }} />
          <span style={{ color: theme.inkFaint }}>Stylistic — not weaker/stronger</span>
        </div>
      )}
    </div>
  );
}

/** Format the raw metric value for the row's value column (matches old app). */
function fmtValue(v: number | null, outputType?: string | null): string {
  if (v === null) return "—";
  if (outputType === "percentage") return (v * 100).toFixed(2);
  if (outputType === "integer") return v.toFixed(0);
  return v.toFixed(2);
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
